"use client"

import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Label } from "./label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

export interface DatePickerProps {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  label?: string
  /** Custom display formatter for the selected date. Falls back to date-fns format "PPP". */
  formatDisplay?: (date: Date) => string
}

function DatePicker({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  label,
  formatDisplay,
}: DatePickerProps) {
  const { t } = useTranslation("common")
  const placeholderText = placeholder ?? t("app.pickADate")
  const picker = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? (formatDisplay ? formatDisplay(value) : format(value, "PPP")) : <span>{placeholderText}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )

  if (label) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        {picker}
      </div>
    )
  }

  return picker
}

export { DatePicker }
